<script setup lang="ts">
import { AanvragenDomainEntitiesMuterenMutatieActie as MutatieActie } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMuterenMutatieActie';
import { type AanvragenMuterenRoyementDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenRoyementDto';
import { type AanvragenPolissenGetPakketPolissenByRelatieSubPolisDto as PolisDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetPakketPolissenByRelatieSubPolisDto';
import { SUREbusinessCommonDomainSoortVerzekeringEnum as SoortVerzekering } from '@services/polis-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import VMenu from '@shared/component/VMenu/VMenu.vue';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { computed, ref, toRefs } from 'vue';
import { tryFormatDate } from '@tools/dates';
import { useProductenGetByPakket } from '@services/producten-public/orval-api/producten-api/producten-api';
import { hasPermission } from '@permission/hasPermission';
import VCollapsibleSection from '@shared/component/VCollapsibleSection/VCollapsibleSection.vue';
import SubpolisMuterenModal from './modals/SubpolisMuterenModal.vue';
import SubpolisRoyerenModal from './modals/SubpolisRoyerenModal.vue';
import SubpolisIngangsdatumModal from './modals/SubpolisIngangsdatumModal.vue';
import { usePakketpolisStore } from './pakketpolisStore';
import SubpolisRow from './SubpolisRow.vue';

const props = defineProps<{
  title: string;
  pakketpolisnummer: string;
  polissen: PolisDto[];
  pakketId?: string | null;
  adviseurnummer: number;
  showNieuweProducten: boolean;
  hideOptions?: boolean;
}>();

const { title, polissen, pakketpolisnummer, pakketId, adviseurnummer, showNieuweProducten } =
  toRefs(props);

const notification = useNotification();

const store = usePakketpolisStore();

const open = defineModel<boolean>('open', { required: true });

const menus = ref<InstanceType<typeof VMenu>[]>([]);

const pakketpolis = computed(() => store.getPakketpolis(pakketpolisnummer.value));

const magMutatieStarten = hasPermission('Muteren.MutatieStarten');
const magRoyeren = hasPermission('Muteren.Royeren');

const { data: producten } = useProductenGetByPakket(
  computed(() => ({ pakketId: pakketId.value!, adviseurnummer: adviseurnummer.value })),
  {
    query: {
      enabled: () => !!pakketId.value && !!adviseurnummer.value,
      select: (q) => q.result,
    },
  },
);

const isSubpolisMuterenModalOpen = ref<boolean>(false);
const isSubpolisRoyerenModalOpen = ref<boolean>(false);
const isSubpolisIngangsdatumModalOpen = ref<boolean>(false);
const selectedPolisnummer = ref<string>();
const selectedProductmaatschappijId = ref<string>();
const selectedSoortVerzekering = ref<SoortVerzekering>();
const selectedPolisRoyeerActie = ref<AanvragenMuterenRoyementDto>();

type MenuItem = {
  polisnummer: string;
  items: {
    label: string;
    disabled: boolean;
    reden: string | null | undefined;
    command: () => void;
    icon: string;
  }[];
};

function openModal(polisnummer: string, type: 'muteren' | 'royeren' | 'ingangsdatum') {
  const polis = pakketpolis.value?.originelePakketpolis.subpolissen.find(
    (s) => s.polisnummer === polisnummer,
  );
  if (!polis) {
    return notification.error('De betreffende subpolis kon niet worden gevonden.');
  }
  selectedSoortVerzekering.value = polis.soortVerzekering;
  selectedPolisnummer.value = polisnummer;
  selectedProductmaatschappijId.value = polis.productmaatschappijId ?? undefined;

  const mutatieActies = pakketpolis.value!.mutatieActies.subpolisMutatieActies.find(
    (x) => x.polisnummer === polisnummer,
  );
  if (!mutatieActies) {
    return notification.error('Er ging iets mis bij het inladen van de subpolis.');
  }
  selectedPolisRoyeerActie.value = mutatieActies.royeren;

  if (type === 'muteren') {
    isSubpolisMuterenModalOpen.value = true;
  } else if (type === 'ingangsdatum') {
    isSubpolisIngangsdatumModalOpen.value = true;
  } else {
    isSubpolisRoyerenModalOpen.value = true;
  }
}

const menuOptions = computed<MenuItem[] | undefined>(() =>
  pakketpolis.value?.mutatieActies.subpolisMutatieActies.map((subActies) => ({
    polisnummer: subActies.polisnummer,
    items: [
      {
        label: 'Muteren',
        disabled: !subActies.muteren.isMogelijk,
        reden: subActies.muteren.melding,
        command: () => openModal(subActies.polisnummer, 'muteren'),
        icon: 'far fa-pen',
      },
      {
        label: 'Royeren',
        disabled: !subActies.royeren.isMogelijk,
        reden: subActies.royeren.melding,
        command: () => openModal(subActies.polisnummer, 'royeren'),
        icon: 'far fa-times',
      },
      {
        label: 'Muteer ingangsdatum',
        disabled: !subActies.ingangsdatum.isMogelijk,
        reden: subActies.ingangsdatum.melding,
        command: () => openModal(subActies.polisnummer, 'ingangsdatum'),
        icon: 'far fa-calendar-lines-pen',
      },
    ],
  })),
);

function toggleMenu(idx: number, event: MouseEvent) {
  // alle menu's m.u.v. zichzelf sluiten
  menus.value.forEach((m, midx) => {
    if (midx !== idx) m.hide(event);
  });
  menus.value[idx].toggle(event);
}

const pakketpolisStore = usePakketpolisStore();

const gemuteerdePolissen = computed(
  () => pakketpolisStore.getPakketpolis(pakketpolisnummer.value)?.subpolissen,
);

function getPolisIcon(soortVerzekering?: SoortVerzekering): string {
  switch (soortVerzekering) {
    case SoortVerzekering.MR:
    case SoortVerzekering.VR:
    case SoortVerzekering.TR:
      return 'fas fa-car';
    case SoortVerzekering.BR:
      return 'fas fa-house';
    default:
      return 'fa-regular fa-file-lines';
  }
}

function heeftMutatie(polisnummer: string) {
  return !!gemuteerdePolissen.value?.find((x) => x?.polisnummer === polisnummer)?.actie;
}

function getProduct(productId: string) {
  return producten.value?.flatMap((x) => x.products).find((x) => x.productId === productId);
}

function getRedenTekst(polisnummer: string, uitgebreid: boolean = false) {
  const polis = gemuteerdePolissen.value?.find((x) => x?.polisnummer === polisnummer);
  if (!polis) return undefined;
  const prefix = polis.actie !== MutatieActie.MuterenIngangsdatum ? 'per ' : '';
  const datum = polis.mutatiedatum ? ` ${prefix}${tryFormatDate(polis.mutatiedatum)}` : '';
  const actie = polis.actie === MutatieActie.MuterenIngangsdatum ? 'Ingangsdatum' : polis.actie;
  return `${uitgebreid ? polis.mutatiereden : actie}${datum}`;
}
</script>

<template>
  <VCollapsibleSection v-model:open="open" class="px-6">
    <template #title>
      <div class="px-2">{{ title }}</div>
      <VStatusTag color="grey">{{ polissen.length }}</VStatusTag>
    </template>
    <template #content>
      <ul class="divide-y">
        <li
          v-for="(polis, idx) in polissen"
          :key="polis.polisnummer"
          :data-testid="`subpolis-${polis.polisnummer}`"
          class="border-0 border-solid border-grey-200 py-2"
        >
          <SubpolisRow
            :identifier="polis.polisnummer"
            :title="polis.omschrijving"
            :sub-text="polis.kenmerk"
            :icon="getPolisIcon(polis.soortVerzekering)"
            :danger-label="polis.isGeschorst ? 'Geschorst' : undefined"
            :mutatie="
              heeftMutatie(polis.polisnummer)
                ? {
                    reden: getRedenTekst(polis.polisnummer),
                    redenUitgebreid: getRedenTekst(polis.polisnummer, true),
                  }
                : undefined
            "
            :link="{ name: 'detailsAlgemeen', params: { polisnummer: polis.polisnummer } }"
          >
            <template #mutatieActie>
              <FontAwesomeIcon
                class="cursor-pointer"
                @click="pakketpolisStore.annuleerMutatie(pakketpolisnummer, polis.polisnummer)"
                icon="far fa-trash"
              />
            </template>
            <template #end>
              <div
                v-if="
                  !polis.isGeroyeerd &&
                  menuOptions &&
                  menuOptions.filter((x) => x.polisnummer === polis.polisnummer)?.length > 0
                "
              >
                <VTooltip label="Opties" v-if="(magRoyeren || magMutatieStarten) && !hideOptions">
                  <button
                    @click="(e) => toggleMenu(idx, e)"
                    :data-testid="`opties-${polis.polisnummer}`"
                    type="button"
                    aria-haspopup="menu"
                    aria-controls="overlay_menu"
                    class="flex h-9 w-8 cursor-pointer items-center justify-center rounded border-none hover:bg-grey-100 focus:outline-none"
                    :class="{ 'bg-grey-100': menus[idx]?.overlayVisible() }"
                  >
                    <FontAwesomeIcon icon="fas fa-ellipsis-vertical" class="text-primary-900" />
                  </button>
                </VTooltip>
                <VMenu
                  popup
                  ref="menus"
                  :model="menuOptions.filter((x) => x.polisnummer === polis.polisnummer)"
                >
                  <template #item="{ item }">
                    <VTooltip
                      :label="item.reden"
                      :active="item.disabled && !!item.reden"
                      class="w-full"
                    >
                      <div
                        class="flex items-center overflow-hidden p-2"
                        :class="{ 'cursor-not-allowed text-grey': item.disabled }"
                      >
                        <FontAwesomeIcon
                          class="mr-2 shrink-0 [--fa-fw-width:16px]"
                          fixed-width
                          v-if="item.icon"
                          :icon="item.icon"
                        />
                        <div>{{ item.label }}</div>
                      </div>
                    </VTooltip>
                  </template>
                </VMenu>
              </div>
            </template>
          </SubpolisRow>
        </li>
        <template v-if="showNieuweProducten">
          <li
            class="border-0 border-solid border-grey-200 py-2"
            v-for="product in pakketpolis?.nieuweProducten"
            :key="product.productId"
          >
            <SubpolisRow
              :title="getProduct(product.productId)?.omschrijving ?? product.productId"
              :icon="
                getPolisIcon(
                  getProduct(product.productId)?.soortVerzekering?.displayName as SoortVerzekering,
                )
              "
              :mutatie="{ reden: 'Nieuw product' }"
            >
              <template #mutatieActie>
                <FontAwesomeIcon
                  class="cursor-pointer"
                  @click="pakketpolisStore.removeProduct(pakketpolisnummer, product.productId)"
                  icon="far fa-trash"
                />
              </template>
            </SubpolisRow>
          </li>
        </template>
      </ul>
      <slot name="footer"></slot>
    </template>
  </VCollapsibleSection>
  <SubpolisMuterenModal
    v-if="selectedPolisnummer && selectedSoortVerzekering"
    v-model:is-open="isSubpolisMuterenModalOpen"
    :soort-verzekering="selectedSoortVerzekering"
    :polisnummer="selectedPolisnummer"
    :productmaatschappij-id="selectedProductmaatschappijId"
    :pakketpolisnummer="pakketpolisnummer"
  />
  <SubpolisRoyerenModal
    v-if="selectedPolisnummer && selectedSoortVerzekering"
    v-model:is-open="isSubpolisRoyerenModalOpen"
    :soort-verzekering="selectedSoortVerzekering"
    :polisnummer="selectedPolisnummer"
    :pakketpolisnummer="pakketpolisnummer"
    :royeer-informatie="selectedPolisRoyeerActie!"
  />
  <SubpolisIngangsdatumModal
    v-if="selectedPolisnummer"
    v-model:is-open="isSubpolisIngangsdatumModalOpen"
    :pakketpolisnummer="pakketpolisnummer"
    :polisnummer="selectedPolisnummer"
  />
</template>
